import { FC, useEffect, useState } from 'react';
import { Modal, Button, Group, Text, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { Patient } from '@medplum/fhirtypes';
import { useMedplumSearch } from '@hooks/useMedplumSearch';
import { CreatePatientField } from './CreatePatientField';
import { FieldItem } from '@configuration/components/choremeioFields.config';
import { getDisplayString } from '@medplum/core';
import { IDENTIFIERS } from '@consts/systems.consts';
import { validateAmka as validateAmkaFormat } from '@utils/amka-validation';

// -----------------------------------------------------------------------------
interface AmkaValidationModalProps {
  opened: boolean;
  onClose: () => void;
  onValidated: (amka: string) => void;
  onRedirectToPatient: (patient: Patient) => void;
}

// -----------------------------------------------------------------------------
type ValidationStep = 'input' | 'checking' | 'exists';

// -----------------------------------------------------------------------------
export const AmkaValidationModal: FC<AmkaValidationModalProps> = ({
  opened,
  onClose,
  onValidated,
  onRedirectToPatient,
}) => {
  const { t } = useTranslation(['create-patient-modal']);
  const { findPersonByAmka } = useMedplumSearch();

  const [validationStep, setValidationStep] = useState<ValidationStep>('input');
  const [existingPatient, setExistingPatient] = useState<Patient | null>(null);

  const validationForm = useForm({
    initialValues: { amka: '' },
    validate: {
      amka: (value: string) => {
        if (!value) {
          return t('errors.amka', { ns: 'create-patient-modal' });
        }
        if (!validateAmkaFormat(value)) {
          return t('errors.invalidAmka', { ns: 'create-patient-modal' });
        }
        return null;
      },
    },
  });

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!opened) {
      resetValidationState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // ---------------------------------------------------------------------------
  const resetValidationState = () => {
    setValidationStep('input');
    setExistingPatient(null);
    validationForm.reset();
  };

  // ---------------------------------------------------------------------------
  const validateAmka = async (values: { amka: string }) => {
    setValidationStep('checking');

    try {
      const existingPatient = await findPersonByAmka(values.amka);

      if (existingPatient) {
        setExistingPatient(existingPatient);
        setValidationStep('exists');
      } else {
        onValidated(values.amka);
        onClose();
      }
    } catch (error) {
      console.error('Error checking AMKA:', error);
      setValidationStep('input');
      validationForm.setFieldError(
        'amka',
        'Error validating AMKA. Please try again.'
      );
    }
  };

  // ---------------------------------------------------------------------------
  const redirectToExistingPatient = () => {
    if (existingPatient) {
      onRedirectToPatient(existingPatient);
      onClose();
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={400}
      title={
        <Text fw={700} size="lg">
          {t('labels.validateIdentifierTitle', 'Validate AMKA', {
            ns: 'create-patient-modal',
          })}
        </Text>
      }
      centered
    >
      {validationStep === 'input' && (
        <form onSubmit={validationForm.onSubmit(validateAmka)}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t(
                'descriptions.validateIdentifierDesc',
                'Please enter the AMKA number to validate',
                { ns: 'create-patient-modal' }
              )}
            </Text>

            <CreatePatientField
              field={
                {
                  type: 'field',
                  fieldName: 'amka',
                  fieldType: 'amka',
                  required: true,
                } as FieldItem
              }
              form={validationForm}
              placeholderAppear={true}
            />

            <Group mt="md">
              <Button variant="outline" onClick={onClose}>
                {t('labels.cancel', { ns: 'create-patient-modal' })}
              </Button>
              <Button type="submit">
                {t('labels.validate', 'Validate', {
                  ns: 'create-patient-modal',
                })}
              </Button>
            </Group>
          </Stack>
        </form>
      )}

      {validationStep === 'checking' && (
        <Stack gap="md" align="center">
          <Text size="sm">
            {t('descriptions.checkingAmka', 'Checking AMKA...', {
              ns: 'create-patient-modal',
            })}
          </Text>
        </Stack>
      )}

      {validationStep === 'exists' && existingPatient && (
        <Stack gap="md">
          <Text size="sm" c="orange">
            {t(
              'descriptions.patientExists',
              'A patient with this AMKA already exists:',
              { ns: 'create-patient-modal' }
            )}
          </Text>

          <Stack gap="xs" p="md" bg="gray.0" style={{ borderRadius: '8px' }}>
            <Text fw={500}>{getDisplayString(existingPatient)}</Text>
            <Text size="sm" c="dimmed">
              AMKA:{' '}
              {
                existingPatient.identifier?.find(
                  (id) => id.system === IDENTIFIERS.AMKA
                )?.value
              }
            </Text>
            {existingPatient.birthDate && (
              <Text size="sm" c="dimmed">
                {`${t('labels.birthdate', { ns: 'create-patient-modal' })}: ${existingPatient.birthDate}`}
              </Text>
            )}
          </Stack>

          <Group mt="md">
            <Button variant="outline" onClick={resetValidationState}>
              {t('buttons.back', { ns: 'common' })}
            </Button>
            <Button onClick={redirectToExistingPatient}>
              {t('labels.goToPatient', 'Go to Patient', {
                ns: 'create-patient-modal',
              })}
            </Button>{' '}
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
